import { useEffect, useState, type CSSProperties } from "react";
import { WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";

interface ProgressEvent {
  type: "progress" | "completed" | "failed";
  simulationId: string;
  percent: number;
  step: string;
  errorMessage?: string;
}

interface Props {
  simulationId: string;
  status: "DRAFT" | "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED";
  onTerminal?: () => void;
}

export function SimulationProgress({ simulationId, status, onTerminal }: Props) {
  const [percent, setPercent] = useState(status === "QUEUED" ? 0 : null);
  const [step, setStep] = useState<string>(status === "QUEUED" ? "Aguardando execução…" : "");
  const [error, setError] = useState<string | null>(null);
  const [streamError, setStreamError] = useState(false);

  useEffect(() => {
    if (status !== "QUEUED" && status !== "RUNNING") return;

    const src = new EventSource(`/api/v1/simulations/${simulationId}/progress`, {
      withCredentials: true,
    });

    // Conexão (re)estabelecida com sucesso: limpa o aviso de "pode estar desatualizado".
    src.onopen = () => setStreamError(false);

    src.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data as string) as ProgressEvent;
        setPercent(evt.percent);
        setStep(evt.step);
        if (evt.type === "failed") {
          setError(evt.errorMessage ?? "Falha desconhecida.");
        }
        if (evt.type === "completed" || evt.type === "failed") {
          src.close();
          onTerminal?.();
        }
      } catch {
        // Evento SSE ilegível: não trava a UI, só sinaliza que o progresso pode
        // estar desatualizado (a barra mantém o último valor conhecido).
        setStreamError(true);
      }
    };

    // Conexão caiu (rede/servidor): sem fallback de polling neste componente, então
    // só torna visível que o progresso pode estar parado — evita passar confiança falsa.
    src.onerror = () => setStreamError(true);

    return () => src.close();
  }, [simulationId, status, onTerminal]);

  if (status === "COMPLETED" || status === "FAILED") return null;

  return (
    <div className="space-y-2 rounded-md border bg-card p-3">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{step || "Conectando…"}</span>
        <span className="font-mono">{percent ?? 0}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            "h-full w-[var(--bar-w)] rounded-full transition-all",
            error ? "bg-destructive" : "bg-primary"
          )}
          style={{ "--bar-w": `${percent ?? 0}%` } as CSSProperties}
          role="progressbar"
          aria-valuenow={percent ?? 0}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Progresso da simulação"
        />
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {streamError && !error ? (
        <p className="flex items-center gap-1.5 text-xs text-amber-600">
          <WifiOff className="h-3.5 w-3.5" />
          Conexão perdida — o progresso acima pode estar desatualizado.
        </p>
      ) : null}
    </div>
  );
}
